<template>
  <div class="view-wrapper">
    <span v-if="!isAuthenticated">
      Bitte anmelden, um die Applikation zu verwenden.
    </span>

    <template v-else>
      <BackButton />

      <h2 class="page-title">
        Nicht zurückgegebene und defekte Leihartikel ({{ loansView.length }})
      </h2>

      <Card class="table-card">
        <template #content>
          <div class="table-toolbar">
            <IconField icon-position="right" class="global-search">
              <InputText
                v-model="globalFilterValue"
                placeholder="Suchbegriff eingeben..."
              />

              <InputIcon
                v-if="globalFilterValue"
                class="pi pi-times"
                @click="clearGlobalSearch"
                style="cursor: pointer"
              />
            </IconField>

            <Button
              label="Daten exportieren"
              icon="pi pi-download"
              @click="exportExcel"
            />
          </div>

          <div v-if="loadError" class="error">
            {{ loadError }}
          </div>

          <DataTable
            :value="loansView"
            v-model:filters="filters"
            :global-filter-fields="globalFilterFields"
            data-key="rowKey"
            table-layout="auto"
            paginator
            :rows="10"
            :rows-per-page-options="[5, 10, 20, 50]"
            scrollable
            show-gridlines
            striped-rows
            removable-sort
            :loading="isLoading"
          >
            <template #empty>
              <div style="text-align: center; padding: 0.5rem">
                Keine Leihartikel im Umlauf.
              </div>
            </template>

            <Column
              field="idProductDisplay"
              header="Artikel"
              sortable
              filter
              :show-filter-menu="false"
              filter-placeholder="Filtern..."
            />

            <Column
              field="productName"
              header="Bezeichnung"
              sortable
              filter
              :show-filter-menu="false"
              filter-placeholder="Filtern..."
            />

            <Column
              field="reasonText"
              header="Bewegung"
              sortable
              sort-field="reasonText"
              filter
              :show-filter-menu="false"
              filter-placeholder="Filtern..."
            />

            <Column
              field="eventTime"
              header="Zeitpunkt"
              sortable
              filter-field="eventTimeFormatted"
              filter
              :show-filter-menu="false"
              filter-placeholder="Filtern..."
            >
              <template #body="{ data }">
                {{ formatIsoDateTime(data.eventTime, "") }}
              </template>
            </Column>

            <Column
              field="employeeName"
              header="Benutzer:in"
              sortable
              filter
              :show-filter-menu="false"
              filter-placeholder="Filtern..."
            />
          </DataTable>
        </template>
      </Card>
    </template>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref, watch } from "vue";
  import { writeFile, utils } from "xlsx";
  import { useIsAuthenticated } from "@/composition-api/useIsAuthenticated";
  import Card from "primevue/card";
  import DataTable from "primevue/datatable";
  import Column from "primevue/column";
  import InputText from "primevue/inputtext";
  import IconField from "primevue/iconfield";
  import InputIcon from "primevue/inputicon";
  import Button from "primevue/button";
  import BackButton from "@/components/BackButton.vue";
  import { useBackendApi } from "@/composition-api/useBackendApi";
  import type { OrsyItemOnLoan } from "@apis/backend";
  import { formatIsoDateTime } from "@/utils/dateFormats";
  import { reasonMap } from "@/types/reasonMap";

  const isAuthenticated = useIsAuthenticated();
  const apis = useBackendApi();

  const isLoading = ref(false);
  const loadError = ref<string | null>(null);

  const loans = ref<OrsyItemOnLoan[]>([]);

  const loansView = computed(() =>
    loans.value.map((m) => ({
      ...m,
      rowKey: `${m.idProduct}__${m.eventTime}`,
      idProductDisplay: m.idProduct
        ?.replace(/^BDS/, "BBW ")
        ?.replace(/^OY/, "OY "),
      eventTimeFormatted: formatIsoDateTime(m.eventTime, ""),
      reasonText: reasonMap[m.reason ?? -1]?.label ?? String(m.reason ?? ""),
    })),
  );

  const globalFilterValue = ref("");

  const clearGlobalSearch = () => {
    globalFilterValue.value = "";
  };

  function exportExcel() {
    const exportRows = [...loansView.value]
      .sort((a, b) =>
        (a.employeeName ?? "").localeCompare(b.employeeName ?? "", "de", {
          sensitivity: "base",
        }),
      )
      .map((row) => ({
        Artikel: row.idProductDisplay ?? "",
        Bezeichnung: row.productName ?? "",
        Bewegung: row.reasonText ?? "",
        Zeitpunkt: row.eventTimeFormatted ?? "",
        "Benutzer:in": row.employeeName ?? "",
      }));

    const worksheet = utils.json_to_sheet(exportRows);
    const workbook = utils.book_new();

    utils.book_append_sheet(workbook, worksheet, "Leihartikel");
    writeFile(workbook, "leihartikel.xlsx");
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const filters = ref<Record<string, any>>({
    global: { value: null, matchMode: "contains" },
    idProduct: { value: null, matchMode: "contains" },
    productName: { value: null, matchMode: "contains" },
    reasonText: { value: null, matchMode: "contains" },
    eventTimeFormatted: { value: null, matchMode: "contains" },
    employeeName: { value: null, matchMode: "contains" },
  });

  const globalFilterFields = [
    "idProduct",
    "productName",
    "reasonText",
    "eventTimeFormatted",
    "employeeName",
  ];

  watch(globalFilterValue, (v) => {
    filters.value.global.value = v?.trim() || null;
  });

  onMounted(() =>
    apis.callWhenTokenInitialized(() => {
      void loadLoans();
    }),
  );

  async function loadLoans() {
    try {
      isLoading.value = true;
      loadError.value = null;

      const data = await apis.reporting.fetchOrsyItemsOnLoan({});

      loans.value = data ?? [];
    } catch (error) {
      console.error("Fehler beim Laden der Leihartikel:", error);
      loadError.value = "Fehler beim Laden der Leihartikel.";
      loans.value = [];
    } finally {
      isLoading.value = false;
    }
  }
</script>

<style scoped lang="scss">
  .view-wrapper {
    width: 99%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    font-family: inherit;
  }

  .page-title {
    margin: 1rem 0 0;
    text-align: left;
  }

  .table-card {
    margin-top: 1.5rem;
    width: 100%;
  }

  .table-toolbar {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    margin-bottom: 0.75rem;
  }

  .toolbar-left {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .months-label {
    font-weight: 600;
    white-space: nowrap;
  }

  .months-select {
    min-width: 14rem;
  }

  .global-search :deep(input) {
    min-width: 22rem;
    font-size: 0.95rem;
    padding: 0.4rem 0.6rem;
    border: 1px solid #c5c5c5;
    border-radius: 4px;
    background: #fff;
    box-sizing: border-box;
  }

  .error {
    width: 100%;
    margin: 0 0 0.75rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid #f3c2c2;
    background: #fff3f3;
    border-radius: 6px;
    box-sizing: border-box;
  }
</style>
